import axios from 'axios';
import { supabase } from '../supabaseClient';
import { RATE_LIMIT_CONFIG } from '../config/rateLimiting';
import { sanitizeString} from '../utils/security';

// Use environment variables for all URLs
const rawBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
const API_BASE_URL = rawBase.replace(/\/+$/, ''); // removes trailing slashes
//const AUDIO_BASE_URL = process.env.REACT_APP_AUDIO_BASE_URL || 'https://example.com';

// Input validation helper
const validateInput = (input, type = 'string') => {
  if (!input) return false;
  
  switch (type) {
    case 'string':
      return typeof input === 'string' && input.trim().length > 0;
    case 'number':
      return !isNaN(input) && Number(input) > 0;
    case 'array':
      return Array.isArray(input) && input.length > 0;
    case 'object':
      return typeof input === 'object' && input !== null;
    default:
      return false;
  }
};

// Sanitize input helper
const sanitizeInput = (input) => {
  if (typeof input === 'string') {
    return sanitizeString(input);
  }
  return input;
};

// Enhanced rate limiter with Supabase RLS support
const rateLimiter = {
  ...RATE_LIMIT_CONFIG.CLIENT,
  tokens: RATE_LIMIT_CONFIG.CLIENT.maxConcurrentRequests,
  lastRefill: Date.now(),
  queue: [],
  processing: false,
  
  // Request tracking
  requestHistory: [],
  historyMaxSize: 100,
  
  // Session tracking
  sessionStartTime: Date.now(),
  sessionRequests: 0,
};

// Enhanced rate limiter functions
const refillTokens = () => {
  const now = Date.now();
  const timePassed = (now - rateLimiter.lastRefill) / 1000;
  const tokensToAdd = Math.floor(timePassed * rateLimiter.refillRate);
  
  if (tokensToAdd > 0) {
    rateLimiter.tokens = Math.min(rateLimiter.maxTokens, rateLimiter.tokens + tokensToAdd);
    rateLimiter.lastRefill = now;
  }
};

const cleanupOldRequests = () => {
  const now = Date.now();
  // Clean up burst window
  rateLimiter.burstWindow = rateLimiter.burstWindow.filter(
    time => (now - time) < rateLimiter.burstWindowDuration
  );
  
  // Reset session if duration exceeded
  if (now - rateLimiter.sessionStartTime > rateLimiter.sessionDuration) {
    rateLimiter.sessionStartTime = now;
    rateLimiter.sessionRequests = 0;
  }
};

const detectSuspiciousPattern = () => {
  if (rateLimiter.requestHistory.length < 10) return false;
  
  const recentRequests = rateLimiter.requestHistory.slice(-10);
  const intervals = [];
  const endpoints = new Set();
  const userAgents = new Set();
  
  // Analyze request patterns
  for (let i = 1; i < recentRequests.length; i++) {
    intervals.push(recentRequests[i].time - recentRequests[i-1].time);
    endpoints.add(recentRequests[i].endpoint);
    if (recentRequests[i].userAgent) {
      userAgents.add(recentRequests[i].userAgent);
    }
  }
  
  // Check for too regular intervals (bot-like behavior)
  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const tooRegular = intervals.every(interval => 
    Math.abs(interval - avgInterval) < 50 // Reduced variance threshold
  );
  
  // Check for too many unique endpoints in short time
  const tooManyEndpoints = endpoints.size > 3;
  
  // Check for missing or suspicious user agents
  const suspiciousUserAgent = userAgents.size === 0 || 
    Array.from(userAgents).some(ua => 
      !ua || 
      ua.includes('bot') || 
      ua.includes('crawler') ||
      ua.includes('spider') ||
      ua.length < 10
    );
  
  // Check for rapid identical requests
  const endpointCounts = {};
  recentRequests.forEach(req => {
    endpointCounts[req.endpoint] = (endpointCounts[req.endpoint] || 0) + 1;
  });
  const maxEndpointCount = Math.max(...Object.values(endpointCounts));
  const tooManySameEndpoint = maxEndpointCount > 3;
  
  return tooRegular || tooManyEndpoints || suspiciousUserAgent || tooManySameEndpoint;
};

const trackRequest = (endpoint) => {
  const now = Date.now();
  const userAgent = navigator.userAgent;
  
  // Add to request history with more context
  rateLimiter.requestHistory.push({ 
    time: now, 
    endpoint,
    userAgent,
    timestamp: new Date().toISOString()
  });
  
  if (rateLimiter.requestHistory.length > rateLimiter.historyMaxSize) {
    rateLimiter.requestHistory.shift();
  }
  
  // Add to burst window with timestamp
  rateLimiter.burstWindow.push({
    time: now,
    endpoint,
    userAgent
  });
  
  // Increment session requests
  rateLimiter.sessionRequests++;
};

const acquireToken = async (endpoint) => {
  cleanupOldRequests();
  refillTokens();
  
  // Check session limit
  if (rateLimiter.sessionRequests >= rateLimiter.maxSessionRequests) {
    throw new Error('Session request limit exceeded. Please try again later.');
  }
  
  // Check burst limit
  if (rateLimiter.burstWindow.length >= rateLimiter.burstLimit) {
    throw new Error('Too many requests. Please slow down.');
  }
  
  // Check for suspicious patterns
  if (detectSuspiciousPattern()) {
    // Add a random delay between 1-3 seconds when suspicious pattern is detected
    const delay = Math.floor(Math.random() * 2000) + 1000;
    await new Promise(resolve => setTimeout(resolve, delay));
    return true;
  }
  
  if (rateLimiter.tokens > 0) {
    rateLimiter.tokens--;
    trackRequest(endpoint);
    return true;
  }
  return false;
};

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const processQueue = async () => {
  if (rateLimiter.processing) return;
  rateLimiter.processing = true;

  while (rateLimiter.queue.length > 0) {
    try {
      if (await acquireToken(rateLimiter.queue[0].endpoint)) {
        const nextRequest = rateLimiter.queue.shift();
        nextRequest.resolve();
      } else {
        await delay(500);
      }
    } catch (error) {
      const nextRequest = rateLimiter.queue.shift();
      nextRequest.reject(error);
    }
  }

  rateLimiter.processing = false;
};

const waitForToken = async (endpoint) => {
  try {
    if (await acquireToken(endpoint)) return;
    
    return new Promise((resolve, reject) => {
      rateLimiter.queue.push({ resolve, reject, endpoint });
      processQueue();
    });
  } catch (error) {
    if (error.message.includes('Suspicious request pattern')) {
      // Add a longer delay for suspicious patterns
      await new Promise(resolve => setTimeout(resolve, 3000));
      return true;
    }
    throw error;
  }
};

// Fallback SAT words data
const fallbackWords = [
  {
    word: "abate",
    ipa: "/əˈbeɪt/",
    pronunciationAudio: "https://example.com/abate.mp3",
    quickDefinition: "to become less intense or widespread",
    quickExample: "The storm began to abate after several hours.",
    partsOfSpeech: ["verb"],
    wordListTags: ["SAT"],
    explanations: {
      teacher_lexi: {
        explanation: "When something abates, it becomes less intense or decreases in amount. Think of a storm that gradually loses its strength.",
        audio_url: "https://example.com/abate_lexi.mp3"
      },
      slang_sam: {
        explanation: "Yo, when something abates, it's like when the party starts dying down - the energy just ain't what it used to be.",
        audio_url: "https://example.com/abate_sam.mp3"
      },
      owlbert: {
        explanation: "Hoo hoo! When something abates, it's like when the wind stops blowing so hard - it gradually becomes less intense.",
        audio_url: "https://example.com/abate_owl.mp3"
      }
    }
  },
  {
    word: "aberrant",
    ipa: "/əˈberənt/",
    pronunciationAudio: "https://example.com/aberrant.mp3",
    quickDefinition: "deviating from the norm",
    quickExample: "His aberrant behavior concerned his friends.",
    partsOfSpeech: ["adjective"],
    wordListTags: ["SAT"],
    explanations: {
      teacher_lexi: {
        explanation: "Something that is aberrant is different from what is normal or expected. It's like when a student who is usually very quiet suddenly starts shouting in class.",
        audio_url: "https://example.com/aberrant_lexi.mp3"
      },
      slang_sam: {
        explanation: "If something's aberrant, it's just not normal, you feel me? Like when your friend who's always late shows up early - that's aberrant behavior right there.",
        audio_url: "https://example.com/aberrant_sam.mp3"
      },
      owlbert: {
        explanation: "Hoo hoo! When something is aberrant, it's like when a night owl like me is awake during the day - it's just not normal behavior!",
        audio_url: "https://example.com/aberrant_owl.mp3"
      }
    }
  }
];

// Simple in-memory cache with improved structure
const cache = {
  words: new Map(),
  examples: new Map(),
  similar: new Map(),
  mnemonics: new Map(),
  wordOfDay: new Map()
};

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

const isCacheValid = (timestamp) => {
  return timestamp && (Date.now() - timestamp) < CACHE_DURATION;
};

const setCacheData = (cacheMap, key, data) => {
  if (!data) return; // Don't cache null/undefined data
  cacheMap.set(key.toLowerCase(), {
    data,
    timestamp: Date.now()
  });
};

const getCacheData = (cacheMap, key) => {
  const cachedData = cacheMap.get(key.toLowerCase());
  if (cachedData && isCacheValid(cachedData.timestamp)) {
    return cachedData.data;
  }
  // Clean up invalid cache entries
  if (cachedData) {
    cacheMap.delete(key.toLowerCase());
  }
  return null;
};

/**
 * Groups similar words by splitting comma-separated strings into arrays
 * @param {string[]} groups - Array of comma-separated word strings
 * @returns {string[][]} Array of word arrays, each containing trimmed words
 */
const groupSimilarWords = (groups) => {
  if (!Array.isArray(groups)) return [];
  return groups
    .filter(str => typeof str === 'string' && str.trim().length > 0)
    .map(str => 
      str
        .split(',')
        .map(w => w.trim())
        .filter(w => w.length > 0)
    )
    .filter(group => group.length > 0);
};

const transformWordData = (data) => ({
  word: data.word,
  ipa: data.ipaus,
  pronunciationAudio: data.pronunciation_audio,
  quickDefinition: data.quick_definition,
  quickExample: data.quick_example,
  partsOfSpeech: data.parts_of_speech,
  wordListTags: data.wordListTags || [],
  meanings: data.meanings || [],
  mnemonics: data.mnemonics || [],
  explanations: {
    teacher_lexi: {
      explanation: data.explanations['Teacher Lexi']?.explanation,
      audio_url: data.explanations['Teacher Lexi']?.audio_url,
    },
    slang_sam: {
      explanation: data.explanations['Slang Sam']?.explanation,
      audio_url: data.explanations['Slang Sam']?.audio_url,
    },
    owlbert: {
      explanation: data.explanations['Owlbert']?.explanation,
      audio_url: data.explanations['Owlbert']?.audio_url,
    }
  }
});

// Add rate limit error handling
const handleRateLimitError = (error) => {
  if (error.response && error.response.status === 429) {
    // Rate limit exceeded
    const retryAfter = error.response.headers['retry-after'] || 60;
    console.warn(`Rate limit exceeded. Retrying after ${retryAfter} seconds.`);
    return new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
  }
  throw error;
};

// Create axios instance with security headers
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Content-Security-Policy': "default-src 'self'",
  },
  timeout: 10000, // 10 second timeout
  maxRedirects: 0, // Prevent redirects
  validateStatus: function (status) {
    return status >= 200 && status < 300; // Only accept 2xx responses
  }
});

// Add request interceptor for additional security
apiClient.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent replay attacks
    config.headers['X-Request-Timestamp'] = Date.now();
    
    // Add request ID for tracking
    config.headers['X-Request-ID'] = crypto.randomUUID();
    
    // Validate URL
    if (!config.url.startsWith(API_BASE_URL)) {
      throw new Error('Invalid API URL');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // Validate response headers
    if (!response.headers['content-type']?.includes('application/json')) {
      throw new Error('Invalid content type');
    }
    return response;
  },
  (error) => {
    if (error.response) {
      // Handle specific error codes
      switch (error.response.status) {
        case 429:
          console.warn('Rate limit exceeded');
          break;
        case 403:
          console.warn('Access forbidden');
          break;
        case 401:
          console.warn('Unauthorized access');
          break;
        default:
          console.warn('An error occurred:', error.response.status);
          break;
      }
    }
    return Promise.reject(error);
  }
);

const api = {
  getWord: async (word) => {
    const cacheKey = word.toLowerCase();
    const cachedData = getCacheData(cache.words, cacheKey);
    
    if (cachedData) {
      return cachedData;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/words/${word}`);
      const transformedData = transformWordData(response.data);
      setCacheData(cache.words, cacheKey, transformedData);
      return transformedData;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error fetching word data');
    }
  },

  getUsageExamples: async (word) => {
    const cacheKey = word.toLowerCase();
    const cachedData = getCacheData(cache.examples, cacheKey);
    
    if (cachedData) {
      return cachedData;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/words/${word}/usage`);
      const transformedData = {
        academic: response.data.academic || [{ text: "No examples available.", isPlaceholder: true }],
        casual: response.data.casual || [{ text: "No examples available.", isPlaceholder: true }],
        business: response.data.business || [{ text: "No examples available.", isPlaceholder: true }]
      };

      setCacheData(cache.examples, cacheKey, transformedData);
      return transformedData;
    } catch (error) {
      console.error('Error fetching usage examples:', error);
      return {
        academic: [{ text: "Failed to load examples.", isPlaceholder: true }],
        casual: [{ text: "Failed to load examples.", isPlaceholder: true }],
        business: [{ text: "Failed to load examples.", isPlaceholder: true }]
      };
    }
  },

  getSimilarWords: async (word) => {
    const cacheKey = word.toLowerCase();
    const cachedData = getCacheData(cache.similar, cacheKey);
    
    if (cachedData) {
      return cachedData;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/words/${word}/similar`);
      const processedData = groupSimilarWords(response.data.similar);
      setCacheData(cache.similar, cacheKey, processedData);
      return processedData;
    } catch (error) {
      console.error('Error fetching similar words:', error);
      return [];
    }
  },

  getRandomWord: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/words/random-word`);
      return response.data.word;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error fetching random word');
    }
  },

  getWordSuggestions: async (prefix) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/words/suggestions/${prefix}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching word suggestions:', error);
      return [];
    }
  },

  getWordOfTheDay: async () => {
    try {
      const cacheKey = `word_of_the_day_${new Date().toISOString().split('T')[0]}`;  // Include date in cache key
      const cachedData = getCacheData(cache.wordOfDay, cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const response = await axios.get(`${API_BASE_URL}/words/word-of-the-day`);
      const transformedData = transformWordData(response.data);
      
      setCacheData(cache.wordOfDay, cacheKey, transformedData);
      
      return transformedData;
    } catch (error) {
      console.error('Error fetching word of the day:', error);
      throw error;
    }
  },


  subscribeToFeatures: async (email) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/subscriptions`, { email });
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        throw new Error("You're already subscribed! We'll keep you updated on new features.");
      }
      throw new Error(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    }
  },

  // Blog API functions
  getBlogPosts: async (publishedOnly = true) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/blog?published_only=${publishedOnly}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch blog posts');
    }
  },

  getBlogPost: async (slug) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/blog/${slug}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch blog post');
    }
  },

  createBlogPost: async (post, token) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/blog`, post, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to create blog post');
    }
  },

  updateBlogPost: async (postId, post, token) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/blog/${postId}`, post, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update blog post');
    }
  },

  deleteBlogPost: async (postId, token) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/blog/${postId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to delete blog post');
    }
  },

  // Function to fetch just the list of SAT words
  getSATWords: async () => {
    try {
      const { data: satWords, error: satWordsError } = await supabase
        .from('sat_words')
        .select('word');

      if (satWordsError) throw satWordsError;
      
      if (!satWords || satWords.length === 0) {
        return fallbackWords.map(word => word.word);
      }

      return satWords.map(item => item.word);
      
    } catch (error) {
      console.error('Error in getSATWords:', error);
      return fallbackWords.map(word => word.word);
    }
  },

  // Function to fetch just the list of TOEFL words
  getTOEFLWords: async () => {
    try {
      const { data: toeflWords, error: toeflWordsError } = await supabase
        .from('toefl_words')
        .select('word')
        .order('word', { ascending: true });

      if (toeflWordsError) throw toeflWordsError;
      
      if (!toeflWords || toeflWords.length === 0) {
        console.log('No TOEFL words found in database');
        return [];
      }

      console.log(`Fetched ${toeflWords.length} TOEFL words from database`);
      return toeflWords.map(item => item.word);
      
    } catch (error) {
      console.error('Error in getTOEFLWords:', error);
      return [];
    }
  },

  // Function to fetch just the list of Oxford 5000 words
  getOxford5000Words: async () => {
    try {
      // First, get the count of total words
      const { count, error: countError } = await supabase
        .from('oxford5000_words')
        .select('*', { count: 'exact', head: true });

      if (countError) throw countError;

      // Then fetch all words in batches
      const batchSize = 1000;
      const totalBatches = Math.ceil(count / batchSize);
      let allWords = [];

      for (let i = 0; i < totalBatches; i++) {
        const { data: batchWords, error: batchError } = await supabase
          .from('oxford5000_words')
          .select('word')
          .order('word', { ascending: true })
          .range(i * batchSize, (i + 1) * batchSize - 1);

        if (batchError) throw batchError;
        
        allWords = [...allWords, ...batchWords];
      }

      if (allWords.length === 0) {
        return [];
      }

      return allWords.map(item => item.word);
      
    } catch (error) {
      console.error('Error in getOxford5000Words:', error);
      return [];
    }
  },

  // Function to fetch full data for a single word
  getSATWordData: async (wordString) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/words/${wordString}`);
      return transformWordData(response.data);
    } catch (error) {
      console.error(`Error fetching word "${wordString}":`, error);
      // Return the fallback word data if available
      const fallbackWord = fallbackWords.find(w => w.word.toLowerCase() === wordString.toLowerCase());
      if (fallbackWord) return fallbackWord;
      throw error;
    }
  },

  // Function to fetch full data for a TOEFL word
  getTOEFLWordData: async (wordString) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/words/${wordString}`);
      return transformWordData(response.data);
    } catch (error) {
      console.error(`Error fetching TOEFL word "${wordString}":`, error);
      throw error;
    }
  },

  // Function to fetch full data for an Oxford 5000 word
  getOxford5000WordData: async (wordString) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/words/${wordString}`);
      return transformWordData(response.data);
    } catch (error) {
      console.error(`Error fetching Oxford word "${wordString}":`, error);
      throw error;
    }
  },

  // Add new function to get word meanings
  getWordMeanings: async (word) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/words/${word}/meanings`);
      return response.data.meanings;
    } catch (error) {
      console.error('Error fetching word meanings:', error);
      return [];
    }
  },

  // Function to fetch mnemonics for a word
  getWordMnemonics: async (word) => {
    const cacheKey = word.toLowerCase();
    const cachedData = getCacheData(cache.mnemonics, cacheKey);
    
    if (cachedData) {
      return cachedData;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/words/${word}/mnemonics`);
      setCacheData(cache.mnemonics, cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching mnemonics:', error);
      return { mnemonics: [] };
    }
  },

  // Function to get words by list ID with retry logic
  getWordsByListId: async (listId) => {
    try {
      const { data: words, error } = await supabase
        .from('word_list_words')
        .select('word')
        .eq('list_id', listId)
        .order('word', { ascending: true });

      if (error) throw error;

      if (!words || words.length === 0) {
        return [];
      }

      return words.map(w => w.word);
    } catch (error) {
      console.error(`Error fetching words for list ${listId}:`, error);
      return [];
    }
  },

  // Function to get list ID by display label
  getListIdByDisplayLabel: async (displayLabel) => {
    try {
      // Get all rows with the display label and filter by category
      const { data, error } = await supabase
        .from('word_lists')
        .select('*')
        .eq('display_label', displayLabel)
        .eq('category', 'Grade Level');

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        throw new Error(`No list found with display label: ${displayLabel}`);
      }

      // Since we might have multiple rows, let's get the first one
      // that matches our criteria
      const wordList = data[0];

      return wordList.id;
    } catch (error) {
      console.error(`Error getting list ID for ${displayLabel}:`, error);
      throw error;
    }
  },

  // Function to get all high school words with parallel loading
  getHighSchoolWords: async () => {
    try {
      const highSchoolListIds = [10, 11, 12, 13, 14, 15, 16];
      
      // Load all lists in parallel with a small delay between batches
      const batchSize = 3;
      const batches = [];
      for (let i = 0; i < highSchoolListIds.length; i += batchSize) {
        const batch = highSchoolListIds.slice(i, i + batchSize);
        const batchPromises = batch.map(listId => 
          api.getWordsByListId(listId).catch(error => {
            console.error(`Error fetching words for list ${listId}:`, error);
            return []; // Return empty array if a list fails
          })
        );
        const batchResults = await Promise.all(batchPromises);
        batches.push(...batchResults);
        if (i + batchSize < highSchoolListIds.length) {
          await delay(500); // Small delay between batches
        }
      }
      
      return batches.flat();
    } catch (error) {
      console.error('Error in getHighSchoolWords:', error);
      throw error;
    }
  },

  // Function to get all middle school words with parallel loading
  getMiddleSchoolWords: async () => {
    try {
      const middleSchoolListIds = [7, 8, 9];
      
      // Load all lists in parallel
      const wordPromises = middleSchoolListIds.map(listId => 
        api.getWordsByListId(listId).catch(error => {
          console.error(`Error fetching words for list ${listId}:`, error);
          return []; // Return empty array if a list fails
        })
      );
      
      const results = await Promise.all(wordPromises);
      return results.flat();
    } catch (error) {
      console.error('Error in getMiddleSchoolWords:', error);
      throw error;
    }
  },

  // Function to get all elementary school words with parallel loading
  getElementaryWords: async () => {
    try {
      const elementaryListIds = [4, 12, 13];  // Correct elementary school list IDs
      
      // Load all lists in parallel with a small delay between batches
      const batchSize = 3;
      const batches = [];
      for (let i = 0; i < elementaryListIds.length; i += batchSize) {
        const batch = elementaryListIds.slice(i, i + batchSize);
        const batchPromises = batch.map(listId => 
          api.getWordsByListId(listId).catch(error => {
            console.error(`Error fetching words for list ${listId}:`, error);
            return []; // Return empty array if a list fails
          })
        );
        const batchResults = await Promise.all(batchPromises);
        batches.push(...batchResults);
        if (i + batchSize < elementaryListIds.length) {
          await delay(500); // Small delay between batches
        }
      }
      
      return batches.flat();
    } catch (error) {
      console.error('Error in getElementaryWords:', error);
      throw error;
    }
  },

  // Update API calls to include validation
  getWordDetails: async (word) => {
    if (!validateInput(word)) {
      throw new Error('Invalid word input');
    }
    
    const sanitizedWord = sanitizeInput(word);
    try {
      await waitForToken('word-details');
      const response = await apiClient.get(`/words/${encodeURIComponent(sanitizedWord)}`)
        .catch(handleRateLimitError);
      return response.data;
    } catch (error) {
      console.error('Error fetching word details:', error);
      throw error;
    }
  },

  // Enhanced request handler with rate limiting
  makeRequest: async (endpoint, options = {}) => {
    try {
      // Check rate limits
      if (!await acquireToken(endpoint)) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }

      // Make the request with timeout
      const response = await Promise.race([
        axios({
          ...options,
          url: `${API_BASE_URL}${endpoint}`,
          timeout: RATE_LIMIT_CONFIG.CLIENT.requestTimeout,
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), RATE_LIMIT_CONFIG.CLIENT.requestTimeout)
        ),
      ]);

      return response.data;
    } catch (error) {
      if (error.response?.status === 429) {
        // Handle rate limit response from server
        const retryAfter = error.response.headers['retry-after'];
        await delay(retryAfter ? parseInt(retryAfter) * 1000 : RATE_LIMIT_CONFIG.CLIENT.retryDelay);
        return api.makeRequest(endpoint, options);
      }
      throw error;
    }
  },
};

export default api;
